/**
 * MONSTER TOWN — THE ERRAND CREW'S ONE INSTALL LINE. ERRAND CREW.
 *
 * `main.ts` calls this once, in the crew band, after the world crew has
 * registered its scenes and after the architect's `installErrands()` has wired
 * the engine itself. Everything this crew owns hangs off here:
 *
 *   giver.ts     whose face is on the card         (a stable monster per giver id)
 *   targets.ts   where the guide arrow points      (a data key -> a point on the glass)
 *   requests.ts  neighbour requests                (checked against the real town)
 *   offers.ts    how an errand arrives             (a wave, a note, a venue)
 *   rewards.ts   what happens when one lands       (coins, bag, cheer)
 *   ui/errands.ts the cards and the arrow          (pictures only)
 *
 * Nothing here registers a save section. That is deliberate and it is the
 * single most important line in this file: the architect's `daySave.ts` already
 * persists errand runs, and this crew derives everything else it needs from
 * that. SAVES ARE SACRED, and the schema this expansion's errand layer adds is
 * zero bytes wide.
 */

import { bus } from '../core/events'
import { save } from '../core/save'
import type { GameContext } from '../app/context'
import { installErrandsUi } from '../ui/errands'
import { installGivers } from './giver'
import { installOffers } from './offers'
import { installRequests } from './requests'
import { installRewards } from './rewards'
import { installTargets } from './targets'
import './events'

/**
 * AN ERRAND THAT MOVED IS A SAVE THAT IS DIRTY. Defence in depth.
 *
 * `core/errands.ts` deliberately does not know that saving exists, and every
 * shipped route into it happens to dirty the save on its own way past: the zoo
 * keeper does `save.request('zoo:talked')`, the cooking sheet does
 * `'cooking:cooked'`, the build crew does it for the placement that answered a
 * neighbour's request. So nothing was actually being lost — but it was true by
 * COINCIDENCE, six crews wide, and the failure it is one omission away from is
 * the worst kind this game has: a kid comes back holding a fruit bowl, with a
 * card telling them to cook a fruit bowl, and no fruit left to do it with. A
 * `make` step is not auto-satisfied from the bag the way `gather` is, so that
 * errand would be genuinely stuck, and LAW #2 says nothing can be stuck.
 *
 * Two lines make it true on purpose instead. `save.request` is debounced 900 ms
 * and deferred across scene transitions, so this costs nothing at all — the
 * placement autosave that was already coming simply carries the errand with it.
 */
function persistProgress(): void {
  bus.on('errand:granted', (e) => save.request(`errand:granted:${e.id}`))
  bus.on('errand:stepDone', (e) => save.request(`errand:step:${e.id}`))
}

export function installErrandCrew(ctx: GameContext): void {
  installGivers()
  installTargets()
  installRequests()
  installRewards()
  installOffers()
  persistProgress()
  installErrandsUi(ctx, ctx.hud)
}
